import { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import n8n from "../../assets/n8n.png";
import dialogflow from "../../assets/dialogflow.png";
import webhooks from "../../assets/webhook.png";
import typebot from "../../assets/typebot.jpg";
import flowbuilder from "../../assets/flowbuilders.png";

import { DeleteOutline, Edit, Add, Search as SearchIcon } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import IntegrationModal from "../../components/QueueIntegrationModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";

const reducer = (state, action) => {
  if (action.type === "LOAD_INTEGRATIONS") {
    const queueIntegration = action.payload;
    const newIntegrations = [];

    queueIntegration.forEach((integration) => {
      const integrationIndex = state.findIndex((u) => u.id === integration.id);
      if (integrationIndex !== -1) {
        state[integrationIndex] = integration;
      } else {
        newIntegrations.push(integration);
      }
    });

    return [...state, ...newIntegrations];
  }

  if (action.type === "UPDATE_INTEGRATIONS") {
    const queueIntegration = action.payload;
    const integrationIndex = state.findIndex(
      (u) => u.id === queueIntegration.id
    );

    if (integrationIndex !== -1) {
      state[integrationIndex] = queueIntegration;
      return [...state];
    } else {
      return [queueIntegration, ...state];
    }
  }

  if (action.type === "DELETE_INTEGRATION") {
    const integrationId = action.payload;

    const integrationIndex = state.findIndex((u) => u.id === integrationId);
    if (integrationIndex !== -1) {
      state.splice(integrationIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const QueueIntegration = () => {
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [queueIntegration, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);

  const { getPlanCompany } = usePlans();
  const companyId = user.companyId;
  const history = useHistory();

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useIntegrations) {
        toast.error(
          "Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando."
        );
        setTimeout(() => {
          history.push(`/`);
        }, 500); // Reduzir delay de 1000ms para 500ms
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchIntegrations = async () => {
        try {
          const { data } = await api.get("/queueIntegration/", {
            params: { searchParam, pageNumber },
          });
          dispatch({
            type: "LOAD_INTEGRATIONS",
            payload: data.queueIntegrations,
          });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchIntegrations();
    }, 200); // Reduzir delay de 500ms para 200ms
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    // const socket = socketManager.GetSocket();

    const onQueueEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({
          type: "UPDATE_INTEGRATIONS",
          payload: data.queueIntegration,
        });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_INTEGRATION", payload: +data.integrationId });
      }
    };

    socket.on(`company-${companyId}-queueIntegration`, onQueueEvent);
    return () => {
      socket.off(`company-${companyId}-queueIntegration`, onQueueEvent);
    };
  }, []);

  const handleOpenUserModal = () => {
    setSelectedIntegration(null);
    setUserModalOpen(true);
  };

  const handleCloseIntegrationModal = () => {
    setSelectedIntegration(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditIntegration = (queueIntegration) => {
    setSelectedIntegration(queueIntegration);
    setUserModalOpen(true);
  };

  const handleDeleteIntegration = async (integrationId) => {
    try {
      await api.delete(`/queueIntegration/${integrationId}`);
      toast.success("Sua integração foi deletada");
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const getIntegrationLogo = (type) => {
    switch (type) {
      case "dialogflow":
        return dialogflow;
      case "n8n":
        return n8n;
      case "webhook":
        return webhooks;
      case "typebot":
        return typebot;
      case "flowbuilder":
        return flowbuilder;
      default:
        return webhooks;
    }
  };

  const getIntegrationTypeName = (type) => {
    switch (type) {
      case "dialogflow":
        return "Dialogflow";
      case "n8n":
        return "N8N";
      case "webhook":
        return "Webhook";
      case "typebot":
        return "Typebot";
      case "flowbuilder":
        return "Flow Builder";
      default:
        return type;
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("queueIntegration.confirmationModal.deleteTitle")} ${
            deletingUser.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteIntegration(deletingUser.id)}
      >
        {i18n.t("queueIntegration.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <IntegrationModal
        open={userModalOpen}
        onClose={handleCloseIntegrationModal}
        aria-labelledby="form-dialog-title"
        integrationId={selectedIntegration && selectedIntegration.id}
      />
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <div style={{
          padding: "24px",
          background: "#f5f7fa",
          minHeight: "calc(100vh - 64px)",
          overflowY: "auto",
        }}>
          {/* Header */}
          <div style={{
            marginBottom: "24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
          }}>
            <div>
              <h1 style={{
                fontSize: "24px",
                fontWeight: 700,
                color: "#1a1a1a",
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}>
                🔗 {i18n.t("queueIntegration.title")}
                <span style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                  color: "white",
                  padding: "4px 12px",
                  borderRadius: "20px",
                }}>
                  {queueIntegration.length} {queueIntegration.length === 1 ? "integração" : "integrações"}
                </span>
              </h1>
              <p style={{
                fontSize: "14px",
                color: "#666",
                margin: "8px 0 0 0",
              }}>
                Configure integrações com sistemas externos e automatize processos
              </p>
            </div>
          </div>

          {/* Search and Add Button */}
          <div style={{
            background: "white",
            borderRadius: "8px",
            padding: "20px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: "1px solid #e8ecf1",
            marginBottom: "24px",
            display: "flex",
            gap: "16px",
            alignItems: "center",
            flexWrap: "wrap",
          }}>
            <div style={{ flex: 1, minWidth: "250px", position: "relative" }}>
              <SearchIcon style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#666",
                fontSize: "20px",
              }} />
              <input
                type="text"
                placeholder={i18n.t("queueIntegration.searchPlaceholder")}
                value={searchParam}
                onChange={handleSearch}
                style={{
                  width: "100%",
                  padding: "12px 16px 12px 44px",
                  border: "1px solid #e8ecf1",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontFamily: "inherit",
                  background: "#fafbfc",
                  transition: "all 0.2s ease",
                }}
                onFocus={(e) => {
                  e.target.style.outline = "none";
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.background = "white";
                  e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e8ecf1";
                  e.target.style.background = "#fafbfc";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
            <button
              onClick={handleOpenUserModal}
              style={{
                padding: "12px 24px",
                background: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#2563eb";
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#3b82f6";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <Add style={{ fontSize: "20px" }} />
              {i18n.t("queueIntegration.buttons.add")}
            </button>
          </div>

          {/* Integrations Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "16px",
            }}
            onScroll={handleScroll}
          >
            {queueIntegration.map((integration) => (
              <div
                key={integration.id}
                style={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "20px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  border: "1px solid #e8ecf1",
                  transition: "all 0.3s ease",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)";
                }}
              >
                {/* Integration Header */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                }}>
                  <img
                    src={getIntegrationLogo(integration.type)}
                    alt={integration.type}
                    style={{
                      width: "140px",
                      height: "40px",
                      objectFit: "contain",
                      borderRadius: "4px",
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#666",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: "4px",
                    }}>
                      ID: {integration.id}
                    </div>
                  </div>
                </div>

                {/* Integration Name */}
                <div>
                  <div style={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#1a1a1a",
                    marginBottom: "4px",
                  }}>
                    {integration.name}
                  </div>
                  <div style={{
                    fontSize: "13px",
                    color: "#666",
                  }}>
                    {getIntegrationTypeName(integration.type)}
                  </div>
                </div>

                {/* Actions */}
                <div style={{
                  display: "flex",
                  gap: "8px",
                  paddingTop: "12px",
                  borderTop: "1px solid #e8ecf1",
                }}>
                  <button
                    onClick={() => handleEditIntegration(integration)}
                    style={{
                      flex: 1,
                      padding: "10px",
                      background: "#f0f9ff",
                      color: "#0369a1",
                      border: "1px solid #bae6fd",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#e0f2fe";
                      e.currentTarget.style.borderColor = "#7dd3fc";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#f0f9ff";
                      e.currentTarget.style.borderColor = "#bae6fd";
                    }}
                  >
                    <Edit style={{ fontSize: "16px" }} />
                    Editar
                  </button>
                  <button
                    onClick={() => {
                      setConfirmModalOpen(true);
                      setDeletingUser(integration);
                    }}
                    style={{
                      flex: 1,
                      padding: "10px",
                      background: "#fef2f2",
                      color: "#dc2626",
                      border: "1px solid #fecaca",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#fee2e2";
                      e.currentTarget.style.borderColor = "#fca5a5";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#fef2f2";
                      e.currentTarget.style.borderColor = "#fecaca";
                    }}
                  >
                    <DeleteOutline style={{ fontSize: "16px" }} />
                    Excluir
                  </button>
                </div>
              </div>
            ))}

            {loading && (
              <div style={{
                gridColumn: "1 / -1",
                textAlign: "center",
                padding: "40px 20px",
              }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  border: "4px solid #e8ecf1",
                  borderTop: "4px solid #3b82f6",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto",
                }} />
              </div>
            )}
          </div>

          {/* Empty State */}
          {queueIntegration.length === 0 && !loading && (
            <div style={{
              textAlign: "center",
              padding: "60px 20px",
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              border: "1px solid #e8ecf1",
            }}>
              <div style={{
                fontSize: "48px",
                marginBottom: "16px",
              }}>
                🔗
              </div>
              <p style={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#1a1a1a",
                marginBottom: "8px",
              }}>
                Nenhuma integração encontrada
              </p>
              <p style={{
                fontSize: "14px",
                color: "#666",
                marginBottom: "24px",
              }}>
                {searchParam
                  ? `Nenhuma integração encontrada para "${searchParam}"`
                  : "Comece adicionando sua primeira integração"}
              </p>
              {!searchParam && (
                <button
                  onClick={handleOpenUserModal}
                  style={{
                    padding: "12px 24px",
                    background: "#3b82f6",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#2563eb";
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#3b82f6";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <Add style={{ fontSize: "20px" }} />
                  Adicionar Integração
                </button>
              )}
            </div>
          )}

          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
    </MainContainer>
  );
};

export default QueueIntegration;
